import { useEffect, useRef } from 'react'
import {
  fetchEntityDetailAndProducts,
  selectContactLocations,
  selectContactLocationsStatus,
  selectLocation,
  selectMapEntityDetails,
  selectMapEntityVariant,
  selectServiceLocationId,
  setSelectedEntityVariant,
  setSelectedServiceLocationId,
} from '@user/store/map'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import { toast } from '@sales-center/components'
import { MapPinProps } from '@user/components/HereMap/MapPin/types'
import { EntityLocationDTO } from '@user/store/map/typings'
import { logger } from '@user/utils/logger'
import { formatAddress } from '@user/features/MapView/utils/formatAddress'

type Props = {
  drawerOpen: boolean
  activePin: EntityLocationDTO | null
  manualRefresh: () => void
  onDrawerClose: () => void
  setClickedPin: (pin: EntityLocationDTO | null) => void
}

// A brief alt-tab shouldn't refire SearchArea; only refresh after a real absence.
const MIN_HIDDEN_MS = 5_000

// Restricts to variants accepted by fetchEntityDetailAndProducts; returns null otherwise.
const toFetchableVariant = (
  raw: string | undefined,
): MapPinProps['variant'] | null => {
  switch (raw?.toLowerCase()) {
    case 'lead':
      return 'lead'
    case 'opportunity':
      return 'opportunity'
    case 'pendinglead':
      return 'pendinglead'
    case 'draftlead':
      return 'draftlead'
    default:
      return null
  }
}

/** Reconciles the focused entity after a cross-tab change (e.g. lead converted to opportunity in another tab) by refetching pins on tab refocus and re-resolving the open drawer's entity. */
export function useRefreshOnTabFocus({
  drawerOpen,
  activePin,
  manualRefresh,
  onDrawerClose,
  setClickedPin,
}: Props) {
  const dispatch = useAppDispatch()
  const contactLocations = useAppSelector(selectContactLocations)
  const contactLocationsStatus = useAppSelector(selectContactLocationsStatus)
  const entityDetails = useAppSelector(selectMapEntityDetails)
  const variant = useAppSelector(selectMapEntityVariant)
  const serviceLocationId = useAppSelector(selectServiceLocationId)
  const serviceLocation = useAppSelector(selectLocation)

  const pendingSyncRef = useRef(false)
  const syncTargetRef = useRef<{
    entityId: number
    serviceLocationId: number | null
    variant: MapPinProps['variant']
    latitude: number | null
    longitude: number | null
    formattedAddress: string | null
  } | null>(null)
  const hiddenAtRef = useRef<number | null>(null)
  const prevStatusRef = useRef(contactLocationsStatus)

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now()
        return
      }

      const hiddenAt = hiddenAtRef.current
      hiddenAtRef.current = null

      if (hiddenAt == null || Date.now() - hiddenAt < MIN_HIDDEN_MS) return

      manualRefresh()
      if (drawerOpen && entityDetails && variant) {
        syncTargetRef.current = {
          entityId: entityDetails.id,
          serviceLocationId: serviceLocationId ?? null,
          variant,
          latitude: activePin?.latitude ?? serviceLocation?.latitude ?? null,
          longitude: activePin?.longitude ?? serviceLocation?.longitude ?? null,
          formattedAddress: formatAddress(
            activePin?.address?.street1 || '',
            activePin?.address?.street2 || '',
            activePin?.address?.city || '',
            activePin?.address?.stateAbbreviation || '',
            activePin?.address?.postalCode || '',
          ),
        }
        pendingSyncRef.current = true
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    manualRefresh,
    drawerOpen,
    entityDetails,
    variant,
    serviceLocationId,
    serviceLocation,
    activePin,
  ])

  // Sync trigger is the loading→success/error transition, not the contactLocations
  // reference — that way an unchanged response still clears the pending flag.
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = contactLocationsStatus

    if (prev !== 'loading') return
    if (
      contactLocationsStatus !== 'success' &&
      contactLocationsStatus !== 'error'
    )
      return

    if (!pendingSyncRef.current || !syncTargetRef.current) return

    pendingSyncRef.current = false
    const target = syncTargetRef.current
    syncTargetRef.current = null

    if (contactLocationsStatus === 'error') return

    const {
      entityId,
      serviceLocationId: slId,
      variant: oldVariant,
      latitude,
      longitude,
      formattedAddress,
    } = target

    logger.log('restore target', target)

    const exactMatch =
      slId != null
        ? contactLocations.find(
            (loc) =>
              loc.entityId === entityId && loc.serviceLocationId === slId,
          )
        : undefined

    if (exactMatch && slId != null) {
      logger.log('found exact match', exactMatch)
      const newVariant = toFetchableVariant(exactMatch.entityType)
      if (!newVariant) {
        toast.info('This record is no longer available.')
        onDrawerClose()
        return
      }
      if (newVariant !== oldVariant) {
        dispatch(setSelectedEntityVariant(newVariant))
      }
      dispatch(fetchEntityDetailAndProducts([newVariant, entityId, slId, true]))
      return
    }

    if (latitude != null && longitude != null) {
      logger.log('no exact match, trying to find nearby pin', {
        latitude,
        longitude,
      })
      const locationMatch = contactLocations.find(
        (loc) =>
          loc.entityId > 0 &&
          Math.abs(loc.latitude - latitude) < 0.00001 &&
          Math.abs(loc.longitude - longitude) < 0.00001,
      )

      if (locationMatch) {
        logger.log('lat/lang matched', locationMatch)
        const newVariant = toFetchableVariant(locationMatch.entityType)
        if (newVariant) {
          dispatch(setSelectedEntityVariant(newVariant))
          dispatch(
            setSelectedServiceLocationId(locationMatch.serviceLocationId),
          )
          dispatch(
            fetchEntityDetailAndProducts([
              newVariant,
              locationMatch.entityId,
              locationMatch.serviceLocationId ?? 0,
              true,
            ]),
          )
          setClickedPin(locationMatch)
          return
        }
      }
    }
    if (formattedAddress) {
      logger.log('no lat/lng match, trying address fallback', {
        formattedAddress,
      })
      const addressMatch = contactLocations.find(
        (loc) =>
          loc.entityId > 0 &&
          loc.address &&
          formatAddress(
            loc.address.street1 || '',
            loc.address.street2 || '',
            loc.address.city || '',
            loc.address.stateAbbreviation || '',
            loc.address.postalCode || '',
          ) === formattedAddress,
      )

      if (addressMatch) {
        logger.log('address matched', addressMatch)
        const newVariant = toFetchableVariant(addressMatch.entityType)
        if (newVariant) {
          dispatch(setSelectedEntityVariant(newVariant))
          dispatch(setSelectedServiceLocationId(addressMatch.serviceLocationId))
          dispatch(
            fetchEntityDetailAndProducts([
              newVariant,
              addressMatch.entityId,
              addressMatch.serviceLocationId ?? 0,
              true,
            ]),
          )
          setClickedPin(addressMatch)
          return
        }
      }
    }

    logger.log('lat/lang and address unmatched, drawer closing')

    toast.info('This record is no longer available.')
    onDrawerClose()
  }, [
    contactLocationsStatus,
    contactLocations,
    dispatch,
    onDrawerClose,
    setClickedPin,
  ])
}
